import { useQuery, useMutation, useQueryClient } from "react-query";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";

export interface PairMapperItem {
  _id: string;
  name: string;
  status: string;
  matchedBy: string;
  matchConfidence?: number;
  keywordOverlap?: string[];
  expiryDate: string;
  category?: string;
  subcategory?: string;
  siteA: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  siteB: {
    platform: string;
    marketId: string;
    marketUrl: string;
  };
  createdAt: string;
}

interface PairsResponse {
  pairs: PairMapperItem[];
  total: number;
}

interface PairStats {
  total: number;
  active: number;
  pending: number;
  keywordPending: number;
  expired: number;
}

export function usePairs(params: {
  status?: string;
  matchedBy?: string;
  search?: string;
  category?: string;
  subcategory?: string;
  runId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery(
    ["prediction-pairs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairsResponse>(
        "/pairs/all",
        { params },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export function usePairStats() {
  return useQuery(["prediction-pair-stats"], async () => {
    const { data } =
      await predictionManagerAxiosApi.get<PairStats>("/pairs/stats");
    return data;
  });
}

export interface PairStatsByCategory {
  total: number;
  byCategory: Record<
    string,
    { total: number; bySubcategory: Record<string, number> }
  >;
}

// Feeds the count badges on the mapper page's category tabs + subcategory
// chips. When `runId` is passed, counts are restricted to pairs created by
// that matching run; otherwise it aggregates across all pairs.
export function usePairStatsByCategory(runId?: string | null) {
  return useQuery<PairStatsByCategory>(
    ["prediction-pair-stats-by-category", runId ?? null],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairStatsByCategory>(
        "/pairs/stats/by-category",
        { params: runId ? { runId } : {} },
      );
      return data;
    },
    { keepPreviousData: true },
  );
}

export function useConfirmPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/confirm`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useRejectPair() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await predictionManagerAxiosApi.patch(`/pairs/${id}/reject`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useFetchMarkets() {
  return useMutation(async () => {
    const { data } = await predictionManagerAxiosApi.post("/matching/fetch");
    return data;
  });
}

export interface RunMatchingOptions {
  fullRun?: boolean;
  skipFetch?: boolean;
  tiers?: string; // e.g. "deterministic", "deterministic,ai_confirmed", "keyword_fallback"
}

export function useRunMatching(defaultOptions: RunMatchingOptions = {}) {
  const qc = useQueryClient();
  return useMutation(
    async (overrides?: RunMatchingOptions) => {
      const opts = { ...defaultOptions, ...overrides };
      const params = new URLSearchParams();
      if (opts.fullRun) params.set("fullRun", "true");
      if (opts.skipFetch) params.set("skipFetch", "true");
      if (opts.tiers) params.set("tiers", opts.tiers);
      const qs = params.toString();
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/run${qs ? `?${qs}` : ""}`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("matching-runs");
      },
    },
  );
}

export interface MatcherScheduleState {
  polymarket: { ruleName: string; state: 'ENABLED' | 'DISABLED' | 'UNKNOWN' };
  kalshi: { ruleName: string; state: 'ENABLED' | 'DISABLED' | 'UNKNOWN' };
  enabled: boolean;
}

export function useMatcherSchedule() {
  return useQuery<MatcherScheduleState>(
    'matcher-schedule-state',
    async () => {
      const { data } = await predictionManagerAxiosApi.get<MatcherScheduleState>(
        '/matching/schedule',
      );
      return data;
    },
    {
      refetchInterval: 30_000,
      staleTime: 15_000,
    },
  );
}

export function useToggleMatcherSchedule() {
  const qc = useQueryClient();
  return useMutation(
    async (nextEnabled: boolean) => {
      const path = nextEnabled
        ? '/matching/schedule/enable'
        : '/matching/schedule/disable';
      const { data } = await predictionManagerAxiosApi.post<MatcherScheduleState>(
        path,
      );
      return data;
    },
    {
      // Optimistic UI: flip immediately, let the response confirm.
      onMutate: async (nextEnabled) => {
        await qc.cancelQueries('matcher-schedule-state');
        const prev = qc.getQueryData<MatcherScheduleState>(
          'matcher-schedule-state',
        );
        if (prev) {
          qc.setQueryData<MatcherScheduleState>('matcher-schedule-state', {
            ...prev,
            enabled: nextEnabled,
          });
        }
        return { prev };
      },
      onError: (_err, _next, ctx: any) => {
        if (ctx?.prev) qc.setQueryData('matcher-schedule-state', ctx.prev);
      },
      onSettled: (data) => {
        if (data) qc.setQueryData('matcher-schedule-state', data);
        else qc.invalidateQueries('matcher-schedule-state');
      },
    },
  );
}

export function useBackfillKeywords() {
  return useMutation(async () => {
    const { data } = await predictionManagerAxiosApi.post(
      "/matching/backfill-keywords",
    );
    return data;
  });
}

export interface CachedMarketResult {
  _id: string;
  platform: string;
  externalId: string;
  title: string;
  eventTitle: string;
  keywords: string[];
  isPaired: boolean;
  endDate: string;
  effectiveEndDate?: string;
}

export function useSearchMarkets(
  q: string,
  platform?: string,
  enabled = true,
) {
  return useQuery(
    ["search-markets", q, platform],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        CachedMarketResult[]
      >("/pairs/search-markets", {
        params: { q, platform, limit: 20 },
      });
      return data;
    },
    { enabled: enabled && q.length >= 2, keepPreviousData: true },
  );
}

// ─── Matching Run History ────────────────────────────────────────────

export interface MatchDecisionItem {
  sourceExternalId: string;
  sourcePlatform: string;
  sourceTitle: string;
  outcome: "matched" | "no_match" | "no_candidates" | "error";
  matchedExternalId?: string;
  matchedPlatform?: string;
  matchedTitle?: string;
  confidence?: number;
  reasoning?: string;
  keywordOverlap?: string[];
  pairId?: string;
  candidatesConsidered: number;
  errorMessage?: string;
}

export interface MatchingRunItem {
  _id: string;
  startedAt: string;
  completedAt?: string;
  status: string;
  trigger: string;
  newMarketsPolymarket: number;
  newMarketsKalshi: number;
  matchesFound: number;
  matchesCreated: number;
  keywordsExtracted: number;
  errors: string[];
  durationMs: number;
  decisions?: MatchDecisionItem[];
}

export function useMatchingRuns(params: { limit?: number; offset?: number }) {
  return useQuery(
    ["matching-runs", params],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<{
        runs: MatchingRunItem[];
        total: number;
      }>("/matching/runs", { params });
      return data;
    },
    { keepPreviousData: true },
  );
}

export function useMatchingRunDetail(runId: string | null) {
  return useQuery(
    ["matching-run-detail", runId],
    async () => {
      const { data } =
        await predictionManagerAxiosApi.get<MatchingRunItem>(
          `/matching/runs/${runId}`,
        );
      return data;
    },
    { enabled: !!runId },
  );
}

export function useRetryErrors() {
  const qc = useQueryClient();
  return useMutation(
    async (runId: string) => {
      const { data } = await predictionManagerAxiosApi.post(
        `/matching/runs/${runId}/retry-errors`,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("matching-runs");
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("prediction-pair-stats-by-category");
      },
    },
  );
}

export function useManualPair() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { polymarketId: string; kalshiId: string }) => {
      const { data } = await predictionManagerAxiosApi.post(
        "/pairs/manual-pair",
        body,
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries("prediction-pairs");
        qc.invalidateQueries("prediction-pair-stats");
        qc.invalidateQueries("search-markets");
      },
    },
  );
}
